import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCreateTaskTimeRegistration, } from '@/shared/hooks/task/registration/useCreateTaskTimeRegistration';
import { useUpdateTaskStatus } from '@/shared/hooks/task/useUpdateTaskStatus';
import { useDataContext } from '@/shared/providers/DataProvider';
import { ModalType, useModalContext } from '@/shared/providers/ModalProvider';
import { useTaskTimerContext } from '@/shared/providers/TaskTimerProvider';
import ToastrService from '@/shared/services/ToastrService';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { DragDropContext, Draggable, Droppable, } from '@hello-pangea/dnd';
import { useIntl } from 'react-intl';
import taskMessages from '@/modules/tasks/taskMessages';
import authService, { functionalities, privileges } from '@/shared/services/AuthService';
import appMessages from '@/shared/messages/appMessages';
import { Method, TaskStatus, TaskType } from './constants';
import { getContainerListStyle, getItemStyle, getKanbanBoardColumns, getListStyle, getSubTaskByStatusMap, } from './helper';
import SubTaskComponent from './task/SubTask';
// eslint-disable-next-line react/prop-types
const KanbanBoard = ({ task, shareItem }) => {
    const intl = useIntl();
    const subTaskByStatusMap = getSubTaskByStatusMap(task.children);
    const initialColumns = getKanbanBoardColumns(subTaskByStatusMap);
    const [columns, setColumns] = useState(initialColumns);
    const { updateTaskStatus } = useUpdateTaskStatus();
    const { createTaskTimeRegistration } = useCreateTaskTimeRegistration();
    const { setShowModalInfo } = useModalContext();
    const { defaultCompany, currentUserId } = useDataContext();
    const { processTimeRegistrationResult } = useTaskTimerContext();
    useEffect(() => {
        const updatedColumns = getKanbanBoardColumns(getSubTaskByStatusMap(task.children));
        setColumns(updatedColumns);
    }, [task.children]);
    const canCreateTask = useMemo(() => authService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Create,
    }), []);
    const canUpdateTask = useMemo(() => authService.hasPermission({
        functionality: functionalities.PlannedTask,
        privilege: privileges.Update,
    }), []);
    const onCreateSubTask = () => {
        const newSubTask = {
            parentId: task.id,
            parent: task,
            name: intl.formatMessage(taskMessages.NewTask),
            deadline: moment
                .utc(task.deadline)
                .utcOffset(0)
                .toISOString(),
            status: 0,
            invoiceStreamId: task.invoiceStreamId,
            relationId: task.relationId,
            relation: task.relation,
            userId: currentUserId,
            taskLevel: 1, // This is a subtask
        };
        setShowModalInfo({ modalType: ModalType.TaskDetail, task: newSubTask });
    };
    const updateStatus = async (subTaskToUpdate, status) => {
        await updateTaskStatus({
            id: subTaskToUpdate.id,
            status,
            isBlocked: subTaskToUpdate.isBlocked,
            inapplicable: false,
            rejected: false, // when dragging the task we'll always clear this status
        });
    };
    const onTaskMailModalClosed = async (subTaskToUpdate, status) => {
        if (status === TaskStatus.InProgress) {
            await updateTaskStatus({
                id: subTaskToUpdate.id,
                status: TaskStatus.Todo,
                isBlocked: subTaskToUpdate.isBlocked,
                inapplicable: subTaskToUpdate.inapplicable,
                rejected: subTaskToUpdate.rejected,
            });
            ToastrService.info(intl.formatMessage(appMessages.msgTaskEmailCancelled));
        }
    };
    const onTaskMailModalSaved = async (subTaskToUpdate) => {
        if (subTaskToUpdate.type !== TaskType.Mail && subTaskToUpdate.type !== TaskType.AutomaticMail)
            return;
        await updateTaskStatus({
            id: subTaskToUpdate.id,
            status: TaskStatus.Done,
            isBlocked: subTaskToUpdate.isBlocked,
            inapplicable: subTaskToUpdate.inapplicable,
            rejected: subTaskToUpdate.rejected,
        });
        if (defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration) {
            if (subTaskToUpdate.defaultDuration && subTaskToUpdate.defaultDuration > 0) {
                const taskTimeRegistration = {
                    plannedTaskId: subTaskToUpdate.id,
                    plannedTaskTemplateId: subTaskToUpdate.templateId,
                    plannedTaskName: subTaskToUpdate.name,
                    invoiceStreamId: subTaskToUpdate.invoiceStreamId,
                    relationId: subTaskToUpdate.relationId,
                    resourceId: subTaskToUpdate.resourceId,
                    durationMinutes: subTaskToUpdate.defaultDuration,
                    mainPlannedTaskId: task.id,
                    mainTaskTemplateId: task.templateId,
                    mainPlannedTaskName: task.name,
                };
                const timeRegistrationResult = await createTaskTimeRegistration(taskTimeRegistration);
                await processTimeRegistrationResult(timeRegistrationResult);
            }
            else {
                ToastrService.info(intl.formatMessage(appMessages.msgNoRegistrationMadeDefaultTimeZero));
            }
        }
        else if (defaultCompany.taskTimeRegistrationMethod === Method.OpenFormOnDone) {
            subTaskToUpdate.relation = task.relation;
            subTaskToUpdate.mainTaskId = task.id;
            subTaskToUpdate.mainTemplateId = task.templateId;
            subTaskToUpdate.assignmentTemplateId = task.assignmentTemplateId;
            setShowModalInfo({ modalType: ModalType.TimeRegistration, task: subTaskToUpdate });
        }
    };
    const onOpenTaskDetails = taskToOpen => {
        setShowModalInfo({
            modalType: ModalType.TaskDetail,
            task: taskToOpen,
            onTaskMailModalSaved: () => onTaskMailModalSaved(taskToOpen),
        });
    };
    const onTaskStateChange = async (subTaskToUpdate, status, prevStatus) => {
        let updateBoard = false;
        if (status === prevStatus)
            return;
        const taskType = subTaskToUpdate.type;
        subTaskToUpdate.isBlocked = false; // when moving a task we'll always unblock it
        if ((taskType === TaskType.Mail || taskType === TaskType.AutomaticMail) &&
            status === TaskStatus.InProgress) {
            subTaskToUpdate.relation = task.relation;
            setShowModalInfo({
                modalType: ModalType.TaskMail,
                task: subTaskToUpdate,
                onSave: () => onTaskMailModalSaved(subTaskToUpdate),
                onClose: () => onTaskMailModalClosed(subTaskToUpdate, status),
            });
            updateBoard = true;
        }
        else if ((taskType === TaskType.Mail || taskType === TaskType.AutomaticMail) &&
            status === TaskStatus.Done) {
            await updateStatus(subTaskToUpdate, status);
            updateBoard = true;
            ToastrService.info(intl.formatMessage(appMessages.taskMgmtSendMailContent), intl.formatMessage(appMessages.taskMgmtSendMailTitle), () => {
                subTaskToUpdate.relation = task.relation;
                setShowModalInfo({
                    modalType: ModalType.TaskMail,
                    task: subTaskToUpdate,
                    onSave: () => onTaskMailModalSaved(subTaskToUpdate),
                    onClose: () => onTaskMailModalClosed(subTaskToUpdate, status),
                });
            });
        }
        else {
            await updateStatus(subTaskToUpdate, status);
            updateBoard = true;
            if (status === TaskStatus.Done &&
                defaultCompany.taskTimeRegistrationMethod === Method.OpenFormOnDone &&
                taskType !== TaskType.Condition) {
                // Move to 'Done' should open registration form
                subTaskToUpdate.relation = task.relation;
                subTaskToUpdate.mainTaskId = task.id;
                subTaskToUpdate.mainTemplateId = task.templateId;
                subTaskToUpdate.assignmentTemplateId = task.assignmentTemplateId;
                setShowModalInfo({ modalType: ModalType.TimeRegistration, task: subTaskToUpdate });
            }
        }
        // Default time duration tasks should create registration automatically when moving to done and time registration method is 'Automatic'
        if (status === TaskStatus.Done &&
            (taskType === TaskType.DefaultTime ||
                taskType === TaskType.Mail ||
                taskType === TaskType.AutomaticMail) &&
            defaultCompany.taskTimeRegistrationMethod === Method.AutomaticRegistration) {
            if (subTaskToUpdate.defaultDuration && subTaskToUpdate.defaultDuration > 0) {
                const taskTimeRegistration = {
                    plannedTaskId: subTaskToUpdate.id,
                    plannedTaskTemplateId: subTaskToUpdate.templateId,
                    plannedTaskName: subTaskToUpdate.name,
                    invoiceStreamId: subTaskToUpdate.invoiceStreamId,
                    relationId: subTaskToUpdate.relationId,
                    resourceId: subTaskToUpdate.resourceId,
                    durationMinutes: subTaskToUpdate.defaultDuration,
                    mainPlannedTaskId: task.id,
                    mainTaskTemplateId: task.templateId,
                    mainPlannedTaskName: task.name,
                };
                const timeRegistrationResult = await createTaskTimeRegistration(taskTimeRegistration);
                await processTimeRegistrationResult(timeRegistrationResult);
            }
            else {
                ToastrService.info(intl.formatMessage(appMessages.msgNoRegistrationMadeDefaultTimeZero));
            }
        }
        return updateBoard;
    };
    const onDragEnd = async (result) => {
        // dropped nowhere
        if (!result.destination) {
            return;
        }
        const { source } = result;
        const { destination } = result;
        const startColumn = columns.find(f => f.id === source.droppableId);
        const finishColumn = columns.find(f => f.id === destination.droppableId);
        // did not move anywhere - can bail early
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
            return;
        }
        // same columns
        if (startColumn === finishColumn) {
            return;
        }
        // NOT same columns
        // Moving from one column to another
        const startItemIds = Array.from(startColumn.itemIds);
        const movingItemId = startColumn.itemIds[source.index];
        const subTaskToUpdate = task.children.find(f => f.id === movingItemId);
        if (!subTaskToUpdate)
            return;
        startItemIds.splice(source.index, 1);
        const newStartColumn = Object.assign(Object.assign({}, startColumn), { itemIds: startItemIds });
        const finishItemIds = Array.from(finishColumn.itemIds);
        finishItemIds.splice(destination.index, 0, movingItemId);
        const newFinishColumn = Object.assign(Object.assign({}, finishColumn), { itemIds: finishItemIds });
        const newColumns = columns.slice();
        const startIndex = columns.findIndex(f => f.id === source.droppableId);
        newColumns[startIndex] = newStartColumn;
        const finishIndex = columns.findIndex(f => f.id === destination.droppableId);
        newColumns[finishIndex] = newFinishColumn;
        const prevStatus = Number(source.droppableId);
        const status = Number(destination.droppableId);
        if (subTaskToUpdate.type === TaskType.Condition && status === TaskStatus.InProgress) {
            ToastrService.info(intl.formatMessage(appMessages.msgTaskOfTypeConditionCannotBeSetInProgress));
            newColumns[startIndex] = startColumn;
            newColumns[finishIndex] = finishColumn;
            setColumns(newColumns);
            return;
        }
        setColumns(newColumns);
        const updateBoard = await onTaskStateChange(subTaskToUpdate, status, prevStatus);
        if (!updateBoard) {
            setColumns(newColumns);
        }
    };
    const reloadColumns = () => {
        const updatedColumns = getKanbanBoardColumns(getSubTaskByStatusMap(task.children));
        setColumns(updatedColumns);
    };
    const getElement = (provided, snapshot, subTask) => (_jsx("div", Object.assign({ ref: provided.innerRef }, provided.draggableProps, provided.dragHandleProps, { style: getItemStyle(snapshot.isDragging, provided.draggableProps.style) }, { children: _jsx("div", { children: _jsx("div", Object.assign({ className: "dskb-content-box" }, { children: _jsx(SubTaskComponent, { isDragging: snapshot.isDragging, task: task, subTask: subTask, shareItem: shareItem, openTaskDetails: onOpenTaskDetails, reloadColumns: reloadColumns }, `${task.id}${subTask.id}`) })) }) })));
    const getItemList = (column, dropProvided, dropSnapshot) => (_jsxs("div", Object.assign({ ref: dropProvided.innerRef }, dropProvided.droppableProps, { style: getListStyle(dropSnapshot.isDraggingOver) }, { children: [column.itemIds.map((itemId, index) => {
                const item = task.children.find(f => f.id === itemId);
                if (!item)
                    return;
                return (_jsx(Draggable, Object.assign({ draggableId: item.id, index: index, isDragDisabled: !canUpdateTask }, { children: (provided, snapshot) => getElement(provided, snapshot, item) }), item.id));
            }), _jsx("div", Object.assign({ style: { width: '25vw' } }, { children: dropProvided.placeholder })), column.id === '0' && canCreateTask && (_jsx("div", { children: _jsx("button", Object.assign({ type: "button", className: "btn btn-default btn-xsm", style: { marginTop: column.itemIds.length > 0 ? '10px' : '0px' }, onClick: onCreateSubTask, title: intl.formatMessage(taskMessages.NewTask) }, { children: _jsx("i", { className: "fa fa-plus" }) })) }))] })));
    const getColumn = (column) => (_jsx("td", Object.assign({ className: "dskb-cell dskb-items-container" }, { children: _jsx("div", Object.assign({ className: "kanban-item-mjr", style: getContainerListStyle() }, { children: _jsx(Droppable, Object.assign({ droppableId: column.id, type: "ITEM", isDropDisabled: !canUpdateTask }, { children: (dropProvided, dropSnapshot) => getItemList(column, dropProvided, dropSnapshot) }), column.id) })) }), column.id));
    return (_jsx(DragDropContext, Object.assign({ onDragEnd: onDragEnd }, { children: columns.map(column => getColumn(column)) })));
};
export default KanbanBoard;
